// Small shared cell / control components used by both the desktop tables
// (CarryoverPanel, MonthTable) and the mobile card list. Extracted into
// one file so imports stay compact.

import { useEffect, useState } from "react";
import { formatMoney } from "@/lib/api";
import { backendToUiStatus, uiToBackendStatus } from "@/lib/bills-util";

export function Th({ children, className = "" }) {
  return (
    <th className={`text-left font-semibold uppercase tracking-widest text-[11px] px-4 py-3 ${className}`}>
      {children}
    </th>
  );
}

export function Td({ children, className = "", ...rest }) {
  return <td className={`px-4 py-3 align-middle ${className}`} {...rest}>{children}</td>;
}

// Inline editable amount — click to edit, enter/blur to save, escape to cancel.
export function EditableAmount({ bill, onSave, accent = "neutral", testId }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(bill.estimated_due_amount));

  useEffect(() => { setValue(String(bill.estimated_due_amount)); }, [bill.estimated_due_amount]);

  const colorCls = {
    unpaid: "text-[#8C5D30] font-semibold",
    paid: "text-[#3B5B3E] font-semibold",
    "not-cleared": "text-[#8C3A30] font-semibold",
    neutral: "text-[#2C302E] font-semibold",
  }[accent];

  const commit = () => {
    setEditing(false);
    onSave(bill, value);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { setEditing(false); setValue(String(bill.estimated_due_amount)); }
        }}
        className="w-24 text-right px-2 py-1 rounded-md border border-[#D2D5D0] bg-white outline-none focus:border-[#4A6148] focus:ring-2 focus:ring-[#4A6148]/20"
        data-testid={testId}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`${colorCls} hover:underline underline-offset-2 decoration-dotted cursor-text`}
      title="Click to edit amount"
      data-testid={testId}
    >
      {formatMoney(bill.estimated_due_amount)}
    </button>
  );
}

// Inline status dropdown for the Bills table. 3 UI options: Unpaid, Pending, Paid.
export function StatusDropdown({ bill, onChange }) {
  const ui = backendToUiStatus(bill.status);
  const styleFor = {
    Unpaid: "bg-[#F2EFEB] text-[#59605C]",
    Pending: "status-short",
    Paid: "status-safe",
  }[ui];
  return (
    <select
      value={ui}
      data-testid={`bill-status-select-${bill.id}`}
      aria-label={`Status for ${bill.name}`}
      onChange={(e) => onChange(bill, uiToBackendStatus(e.target.value))}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border-0 outline-none appearance-none cursor-pointer pr-6 bg-no-repeat ${styleFor}`}
      style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 20 20' fill='%23596059'><path d='M5 8l5 5 5-5H5z'/></svg>\")",
        backgroundPosition: "right 0.4rem center",
      }}
    >
      <option value="Unpaid">Unpaid</option>
      <option value="Pending">Pending</option>
      <option value="Paid">Paid</option>
    </select>
  );
}
